import { Component, Input, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { take, filter } from 'rxjs';
import { SupportModalComponent } from 'src/app/components/modals/support-modal/support-modal.component';
import { ILink } from 'src/app/interfaces/ilink';
import { ControlService } from 'src/app/pages/learner/call/library/control.service';
import { UserService } from 'src/app/services/general/user.service';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';

@Component({
  selector: 'app-sb-navbar',
  templateUrl: './sb-navbar.component.html',
  standalone: false
})
export class SbNavbarComponent {
  @Input() links: ILink[] = [];
  @Input() loginScreen: boolean = false;
  @Input() isQcAdmin: boolean = false;
  @ViewChild('supportModal', { static: true, read: CdkPortalOutlet })
  supportModalPortalOutlet!: CdkPortalOutlet;
  isDropdownMenuOpen: boolean = false;
  isSupportOpen: boolean = false;
  userFirstName: string = '';
  userLastName: string = '';
  userEmail: string = '';
  userNameInitials: string = '';
  componentPortal: ComponentPortal<SupportModalComponent> | null = null;
  currentUrl: string = '';
  hideNavbar: boolean = false;

  constructor(
    private userService: UserService,
    public controlService: ControlService,
    private router: Router,
    private callStore: CallStoreService,
  ) { }

  async ngOnInit() {
    this.validateRoute();
    // ---- Setting User's name and initials for NavBar's profile
    const storedUserFirstName: string | null =
      localStorage.getItem('user_first_name');
    const storedUserLastName: string | null =
      localStorage.getItem('user_last_name');

    // We check if strings are not Empty and that they don't contain only whitespaces
    if (storedUserFirstName && storedUserFirstName.trim()) {
      this.userFirstName = storedUserFirstName;
      this.userNameInitials = Array.from(this.userFirstName.trimStart())[0];
    } else {
      console.warn('Empty/Null or whitespace only User firstName');
    }

    if (storedUserLastName && storedUserLastName.trim()) {
      this.userLastName = storedUserLastName;
      this.userNameInitials =
        this.userNameInitials + Array.from(this.userLastName.trimStart())[0];
    } else {
      console.warn('Empty/Null or whitespace only User lastName');
    }
  }

  openModal() {
    if (this.supportModalPortalOutlet.hasAttached() || this.isSupportOpen) {
      return;
    }

    if (!this.componentPortal) {
      this.componentPortal = new ComponentPortal(SupportModalComponent);
    }

    this.isSupportOpen = true;

    setTimeout(() => {
      const attachedComponentRef =
        this.supportModalPortalOutlet.attachComponentPortal(
          this.componentPortal!
        );
      const wasPaused = this.callStore.getIsPaused();

      if (!wasPaused) {
        this.controlService.pauseSimulation();
      }

      attachedComponentRef.instance.closeModalEvent
        .pipe(take(1))
        .subscribe(() => {
          this.isSupportOpen = false;
          this.supportModalPortalOutlet.detach();

          if (!wasPaused) {
            this.controlService.togglePause();
          }
        });
    }, 0);
  }

  logout(event: Event) {
    event.preventDefault();
    this.userService.logoutRequest().subscribe((response) => {
      console.log(response);
    });
  };

  navigate(url: string): void {
    window.location.assign(url);
  }

  goToAdminPortal() {
    this.navigate(this.userService.getAdminPortalUrl());
  }

  validateRoute(): void {
    // Get the current URL on page load
    this.currentUrl = this.router.url;
    this.hideNavbar = this.currentUrl.includes('/call');

    // Subscribe to route changes and update currentUrl when the URL changes
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: any) => {
        this.currentUrl = event.url;
        this.hideNavbar = this.currentUrl.includes('/call');
      });
  }

  isActive(route: string, childRoutes: string[] = []) {
    const routesToCheck = [route, ...childRoutes];

    return routesToCheck.some((url) => {
      return this.router.isActive(url, {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      });
    });
  }

  get formattedLinks() {
    const links = this.links.map((link) => ({
      text: link.text,
      href: link.route,
      icon: link.icon,
      useRouter: true,
      active: this.isActive(link.route, link.childRoutes),
    }));
    return links;
  }
}
